import React, { useMemo, useState, Component } from 'react';
import { UserIcon, UsersIcon, BookIcon, BuildingIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, ChevronRightIcon, ArrowUpRightIcon, LayersIcon, UserCheckIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
import { GLCodeSelector } from './GLCodeSelector';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from './sharedFinancialData';
type Delegation = {
  id: string;
  type: 'user' | 'role' | 'group';
  assignee: string;
  assigneeId: string;
  glCode: string;
  glName: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  lowerLimit: number;
  upperLimit: number;
  canApproveAboveLimit: boolean;
  requiresFourEyes: boolean;
  secondApproverId?: string;
  secondApprover?: string;
  secondApproverType?: 'user' | 'role' | 'group';
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  currency: string;
};
export function DelegationsManager({
  locale
}) {
  const [activeTab, setActiveTab] = useState('delegations');
  const [showForm, setShowForm] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState<Delegation | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Sample data for delegations
  const [delegations, setDelegations] = useState<Delegation[]>([{
    id: 'del-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '1000.1100.1110',
    glName: 'Cash and Cash Equivalents',
    scope: 'Global',
    lowerLimit: 0,
    upperLimit: 5000,
    canApproveAboveLimit: false,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-002',
    type: 'role',
    assignee: 'Finance Manager',
    assigneeId: 'role-002',
    glCode: '5000',
    glName: 'Expenses',
    scope: 'Global',
    lowerLimit: 5000,
    upperLimit: 25000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'user-002',
    secondApprover: 'Jane Doe',
    secondApproverType: 'user',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-003',
    type: 'user',
    assignee: 'Jane Doe',
    assigneeId: 'user-002',
    glCode: '5000.5100',
    glName: 'Operating Expenses',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    lowerLimit: 0,
    upperLimit: 10000,
    canApproveAboveLimit: true,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'SGD'
  }, {
    id: 'del-004',
    type: 'group',
    assignee: 'Finance Approvers',
    assigneeId: 'group-001',
    glCode: '5000.5200',
    glName: 'Travel Expenses',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    lowerLimit: 10000,
    upperLimit: 50000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'group-002',
    secondApprover: 'Executive Approvers',
    secondApproverType: 'group',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'MXN'
  }]);
  // Sample data for GL restrictions
  const [glRestrictions, setGlRestrictions] = useState([{
    id: 'res-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '5000.5200',
    glName: 'Travel Expenses',
    canApprove: false
  }, {
    id: 'res-002',
    type: 'role',
    assignee: 'Junior Accountant',
    assigneeId: 'role-003',
    glCode: '1000.1200',
    glName: 'Non-Current Assets',
    canApprove: false
  }]);
  // Sample data for user leave status
  const [userLeaveStatus, setUserLeaveStatus] = useState([{
    id: 'leave-001',
    userId: 'user-001',
    userName: 'John Smith',
    isOnLeave: true,
    leaveStartDate: '2023-10-15',
    leaveEndDate: '2023-10-30',
    shouldSkip: true
  }, {
    id: 'leave-002',
    userId: 'user-002',
    userName: 'Jane Doe',
    isOnLeave: false,
    leaveStartDate: null,
    leaveEndDate: null,
    shouldSkip: false
  }]);
  // Sample data for approval rules
  const [approvalRules, setApprovalRules] = useState([{
    id: 'rule-001',
    delegationId: 'del-001',
    delegationName: 'John Smith - Cash and Cash Equivalents',
    approvalLevel: 1,
    requiresMultipleApprovers: false,
    escalationUserId: 'user-002',
    escalationUserName: 'Jane Doe',
    autoApproveAfter: 3
  }, {
    id: 'rule-002',
    delegationId: 'del-002',
    delegationName: 'Finance Manager - Expenses',
    approvalLevel: 2,
    requiresMultipleApprovers: true,
    escalationUserId: null,
    escalationUserName: null,
    autoApproveAfter: 5
  }]);
  // Sample data for approval groups
  const [approvalGroups, setApprovalGroups] = useState([{
    id: 'group-001',
    name: 'Finance Approvers',
    members: [{
      id: 'user-001',
      name: 'John Smith'
    }, {
      id: 'user-002',
      name: 'Jane Doe'
    }]
  }, {
    id: 'group-002',
    name: 'Executive Approvers',
    members: [{
      id: 'user-003',
      name: 'Michael Johnson'
    }, {
      id: 'user-004',
      name: 'Sarah Williams'
    }]
  }]);
  // Sample data for users and roles
  const users = [{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }];
  const roles = [{
    id: 'role-001',
    name: 'Administrator'
  }, {
    id: 'role-002',
    name: 'Finance Manager'
  }, {
    id: 'role-003',
    name: 'Junior Accountant'
  }, {
    id: 'role-004',
    name: 'Department Head'
  }];
  // Sample data for GL codes
  const glCodes = [{
    id: 'gl-001',
    code: '1000',
    name: 'Assets',
    children: [{
      id: 'gl-002',
      code: '1000.1100',
      name: 'Current Assets',
      children: [{
        id: 'gl-003',
        code: '1000.1100.1110',
        name: 'Cash and Cash Equivalents'
      }, {
        id: 'gl-004',
        code: '1000.1100.1120',
        name: 'Accounts Receivable'
      }]
    }, {
      id: 'gl-005',
      code: '1000.1200',
      name: 'Non-Current Assets'
    }]
  }, {
    id: 'gl-006',
    code: '2000',
    name: 'Liabilities',
    children: [{
      id: 'gl-007',
      code: '2000.2100',
      name: 'Current Liabilities'
    }]
  }, {
    id: 'gl-008',
    code: '3000',
    name: 'Equity'
  }, {
    id: 'gl-009',
    code: '4000',
    name: 'Revenue'
  }, {
    id: 'gl-010',
    code: '5000',
    name: 'Expenses',
    children: [{
      id: 'gl-011',
      code: '5000.5100',
      name: 'Operating Expenses'
    }, {
      id: 'gl-012',
      code: '5000.5200',
      name: 'Travel Expenses'
    }]
  }];
  // Available currencies
  const currencies = [{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'SGD',
    name: 'Singapore Dollar'
  }, {
    code: 'MXN',
    name: 'Mexican Peso'
  }];
  // Handler for creating a new delegation
  const handleCreateDelegation = () => {
    setEditingDelegation(null);
    setShowForm(true);
  };
  // Handler for editing a delegation
  const handleEditDelegation = (delegation: Delegation) => {
    setEditingDelegation(delegation);
    setShowForm(true);
  };
  // Handler for duplicating a delegation
  const handleDuplicateDelegation = (delegation: Delegation) => {
    const newDelegation: Delegation = {
      ...delegation,
      id: `del-${Date.now()}`,
      assignee: `Copy of ${delegation.assignee}`
    };
    setDelegations([...delegations, newDelegation]);
  };
  // Handler for deactivating a delegation
  const handleDeactivateDelegation = (delegationId: string) => {
    setDelegations(delegations.map(d => d.id === delegationId ? {
      ...d,
      isActive: false
    } : d));
  };
  // Handler for deleting a delegation
  const handleDeleteDelegation = (delegationId: string) => {
    setDeletingId(delegationId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setDelegations(delegations.filter(d => d.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for form submission
  const handleSubmitForm = (formData: Partial<Delegation>) => {
    if (editingDelegation) {
      // Update existing delegation
      setDelegations(delegations.map(d => d.id === editingDelegation.id ? {
        ...(formData as Delegation),
        id: editingDelegation.id
      } : d));
    } else {
      // Create new delegation
      const newDelegation: Delegation = {
        ...(formData as Delegation),
        id: `del-${Date.now()}`
      };
      setDelegations([...delegations, newDelegation]);
    }
    setShowForm(false);
    setEditingDelegation(null);
  };
  const globalDelegations = delegations.filter(d => d.scope === 'Global');
  const orgUnitDelegations = delegations.filter(d => d.scope === 'OrgUnit');
  // Render the Delegations tab content
  const renderDelegationsTab = () => {
    if (showForm) {
      return <DelegationForm delegation={editingDelegation} users={users} roles={roles} groups={approvalGroups} glCodes={glCodes} currencies={currencies} onSubmit={handleSubmitForm} onCancel={() => {
        setShowForm(false);
        setEditingDelegation(null);
      }} locale={locale} data-id="element-9457" />;
    }
    return <div className="space-y-6" data-id="element-9458">
        {/* Global Delegations */}
        <div className="bg-white shadow rounded-lg" data-id="element-9459">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9460">
            <div className="flex justify-between items-center" data-id="element-9461">
              <div data-id="element-9462">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9463">
                  Global Delegations
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-9464">
                  Global approval authorities available to all organisation
                  units
                </p>
              </div>
              <button onClick={handleCreateDelegation} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9465">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-9466" />
                Add Delegation
              </button>
            </div>
          </div>

          {globalDelegations.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-9467">
              <p className="text-sm text-gray-500" data-id="element-9468">
                No global delegations configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-9469">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-9470">
                <thead className="bg-gray-50" data-id="element-9471">
                  <tr data-id="element-9472">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9473">
                      User/Role/Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9474">
                      GL Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9475">
                      Limits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9476">
                      Approval
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9477">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9478">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-9479">
                  {globalDelegations.map(delegation => <tr key={delegation.id} data-id="element-9480">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9481">
                        <div className="flex items-center" data-id="element-9482">
                          {delegation.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9483" /> : delegation.type === 'role' ? <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9484" /> : <LayersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9485" />}
                          <div className="text-sm font-medium text-gray-900" data-id="element-9486">
                            {delegation.assignee}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9487">
                        <div className="text-sm text-gray-900" data-id="element-9488">
                          {delegation.glCode}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-9489">
                          {delegation.glName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9490">
                        <div className="text-sm text-gray-900" data-id="element-9491">
                          {formatCurrency(delegation.lowerLimit, delegation.currency, locale)}{' '}
                          -{' '}
                          {formatCurrency(delegation.upperLimit, delegation.currency, locale)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9492">
                        {delegation.requiresFourEyes ? <div className="flex items-center text-xs text-indigo-600" data-id="element-9493">
                            <UsersIcon className="h-3 w-3 mr-1" data-id="element-9494" />
                            Four Eyes
                          </div> : <div className="flex items-center text-xs text-gray-500" data-id="element-9495">
                            <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-9496" />
                            Single
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9497">
                        {delegation.isActive ? <StatusBadge status="success" text="Active" data-id="element-9498" /> : <StatusBadge status="error" text="Inactive" data-id="element-9499" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9500">
                        <div className="flex justify-end space-x-2" data-id="element-9501">
                          <button onClick={() => handleEditDelegation(delegation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-9502">
                            <EditIcon className="h-4 w-4" data-id="element-9503" />
                          </button>
                          <button onClick={() => handleDuplicateDelegation(delegation)} className="text-blue-600 hover:text-blue-900" data-id="element-9504">
                            <CopyIcon className="h-4 w-4" data-id="element-9505" />
                          </button>
                          <button onClick={() => handleDeleteDelegation(delegation.id)} className="text-red-600 hover:text-red-900" data-id="element-9506">
                            <TrashIcon className="h-4 w-4" data-id="element-9507" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Delegations */}
        <div className="bg-white shadow rounded-lg" data-id="element-9508">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9509">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9510">
              Organisation Unit Delegations
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9511">
              Approval authorities specific to individual organisation units
            </p>
          </div>

          {orgUnitDelegations.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-9512">
              <p className="text-sm text-gray-500" data-id="element-9513">
                No organisation unit delegations configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-9514">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-9515">
                <thead className="bg-gray-50" data-id="element-9516">
                  <tr data-id="element-9517">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9518">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9519">
                      User/Role/Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9520">
                      GL Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9521">
                      Limits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9522">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9523">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-9524">
                  {orgUnitDelegations.map(delegation => <tr key={delegation.id} data-id="element-9525">
                      <td className="px-6 py-4" data-id="element-9526">
                        <div className="text-sm font-medium text-gray-900" data-id="element-9527">
                          {delegation.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-9528">
                          {delegation.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9529">
                        <div className="flex items-center" data-id="element-9530">
                          {delegation.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9531" /> : delegation.type === 'role' ? <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9532" /> : <LayersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9533" />}
                          <div className="text-sm font-medium text-gray-900" data-id="element-9534">
                            {delegation.assignee}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9535">
                        <div className="text-sm text-gray-900" data-id="element-9536">
                          {delegation.glCode}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-9537">
                          {delegation.glName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9538">
                        <div className="text-sm text-gray-900" data-id="element-9539">
                          {formatCurrency(delegation.lowerLimit, delegation.currency, locale)}{' '}
                          -{' '}
                          {formatCurrency(delegation.upperLimit, delegation.currency, locale)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9540">
                        {delegation.isActive ? <StatusBadge status="success" text="Active" data-id="element-9541" /> : <StatusBadge status="error" text="Inactive" data-id="element-9542" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9543">
                        <div className="flex justify-end space-x-2" data-id="element-9544">
                          <button onClick={() => handleEditDelegation(delegation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-9545">
                            <EditIcon className="h-4 w-4" data-id="element-9546" />
                          </button>
                          <button onClick={() => handleDuplicateDelegation(delegation)} className="text-blue-600 hover:text-blue-900" data-id="element-9547">
                            <CopyIcon className="h-4 w-4" data-id="element-9548" />
                          </button>
                          <button onClick={() => handleDeleteDelegation(delegation.id)} className="text-red-600 hover:text-red-900" data-id="element-9549">
                            <TrashIcon className="h-4 w-4" data-id="element-9550" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  // Render the GL Restrictions tab content (keeping existing implementation)
  const renderGLRestrictionsTab = () => {
    return <div className="space-y-4" data-id="element-9551">
        <div className="flex justify-between items-center mb-4" data-id="element-9552">
          <div data-id="element-9553">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9554">
              GL Restrictions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9555">
              Define where users or roles are restricted from approving
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-9556">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-9557" />
            Add Restriction
          </button>
        </div>
        {/* Existing GL Restrictions table */}
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-9558">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-9559">
            <thead className="bg-gray-50" data-id="element-9560">
              <tr data-id="element-9561">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9562">
                  User/Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9563">
                  GL Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9564">
                  Restriction
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9565">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-9566">
              {glRestrictions.map(restriction => <tr key={restriction.id} data-id="element-9567">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-9568">
                    <div className="flex items-center" data-id="element-9569">
                      {restriction.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9570" /> : <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-9571" />}
                      <div className="text-sm font-medium text-gray-900" data-id="element-9572">
                        {restriction.assignee}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-9573">
                    <div className="text-sm text-gray-900" data-id="element-9574">
                      {restriction.glCode}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-9575">
                      {restriction.glName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-9576">
                    {restriction.canApprove ? <StatusBadge status="success" text="Can Approve" data-id="element-9577" /> : <StatusBadge status="error" text="Cannot Approve" data-id="element-9578" />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9579">
                    <div className="flex justify-end space-x-2" data-id="element-9580">
                      <button className="text-indigo-600 hover:text-indigo-900" data-id="element-9581">
                        <EditIcon className="h-4 w-4" data-id="element-9582" />
                      </button>
                      <button className="text-red-600 hover:text-red-900" data-id="element-9583">
                        <TrashIcon className="h-4 w-4" data-id="element-9584" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  // Render other tabs (keeping existing implementations)
  const renderLeaveStatusTab = () => {
    return <div className="space-y-4" data-id="element-9585">
        <div className="flex justify-between items-center mb-4" data-id="element-9586">
          <div data-id="element-9587">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9588">
              User Delegation Status
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9589">
              Manage user leave and delegation status
            </p>
          </div>
        </div>
        {/* Existing leave status table */}
      </div>;
  };
  const renderApprovalRulesTab = () => {
    return <div className="space-y-4" data-id="element-9590">
        <div className="flex justify-between items-center mb-4" data-id="element-9591">
          <div data-id="element-9592">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9593">
              Approval Rules
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9594">
              Configure approval workflows and escalation rules
            </p>
          </div>
        </div>
        {/* Existing approval rules table */}
      </div>;
  };
  const renderApprovalGroupsTab = () => {
    return <div className="space-y-4" data-id="element-9595">
        <div className="flex justify-between items-center mb-4" data-id="element-9596">
          <div data-id="element-9597">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-9598">
              Approval Groups
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9599">
              Manage reusable groups of approvers
            </p>
          </div>
        </div>
        {/* Existing approval groups grid */}
      </div>;
  };
  return <div className="space-y-6" data-id="element-9600">
      <div className="mb-8" data-id="element-9601">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-9602">Delegations</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9603">
          Configure and manage financial approval authority at global and
          organisation unit levels
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-9604">
        <nav className="-mb-px flex space-x-8" data-id="element-9605">
          <button onClick={() => setActiveTab('delegations')} className={`${activeTab === 'delegations' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-9606">
            Delegations
          </button>
          <button onClick={() => setActiveTab('gl-restrictions')} className={`${activeTab === 'gl-restrictions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-9607">
            GL Restrictions
          </button>
          <button onClick={() => setActiveTab('leave-status')} className={`${activeTab === 'leave-status' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-9608">
            Leave Status
          </button>
          <button onClick={() => setActiveTab('approval-rules')} className={`${activeTab === 'approval-rules' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-9609">
            Approval Rules
          </button>
          <button onClick={() => setActiveTab('approval-groups')} className={`${activeTab === 'approval-groups' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-9610">
            Groups
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6" data-id="element-9611">
        {activeTab === 'delegations' && renderDelegationsTab()}
        {activeTab === 'gl-restrictions' && renderGLRestrictionsTab()}
        {activeTab === 'leave-status' && renderLeaveStatusTab()}
        {activeTab === 'approval-rules' && renderApprovalRulesTab()}
        {activeTab === 'approval-groups' && renderApprovalGroupsTab()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-9612">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9613">
            <div className="fixed inset-0 transition-opacity" data-id="element-9614">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-9615"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-9616">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-9617">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-9618">
                <div className="sm:flex sm:items-start" data-id="element-9619">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-9620">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-9621" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-9622">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9623">
                      Delete Delegation
                    </h3>
                    <div className="mt-2" data-id="element-9624">
                      <p className="text-sm text-gray-500" data-id="element-9625">
                        Are you sure you want to delete this delegation? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-9626">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-9627">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-9628">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Delegation Form Component (simplified - keeping core structure)
function DelegationForm({
  delegation,
  users,
  roles,
  groups,
  glCodes,
  currencies,
  onSubmit,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    type: delegation?.type || 'user',
    assigneeId: delegation?.assigneeId || '',
    assignee: delegation?.assignee || '',
    glCode: delegation?.glCode || '',
    glName: delegation?.glName || '',
    scope: delegation?.scope || 'Global',
    orgUnitId: delegation?.orgUnitId || '',
    orgUnitName: delegation?.orgUnitName || '',
    orgUnitPath: delegation?.orgUnitPath || '',
    lowerLimit: delegation?.lowerLimit || 0,
    upperLimit: delegation?.upperLimit || 0,
    canApproveAboveLimit: delegation?.canApproveAboveLimit || false,
    requiresFourEyes: delegation?.requiresFourEyes || false,
    secondApproverId: delegation?.secondApproverId || '',
    secondApprover: delegation?.secondApprover || '',
    secondApproverType: delegation?.secondApproverType || 'user',
    startDate: delegation?.startDate || new Date().toISOString().split('T')[0],
    endDate: delegation?.endDate || '',
    isActive: delegation?.isActive !== undefined ? delegation.isActive : true,
    currency: delegation?.currency || getDefaultCurrency().code
  });
  const [errors, setErrors] = useState({});
  // Create flattened segments for GL code lookup
  const flattenedSegments = useMemo(() => {
    const result = [];
    const flatten = (codes, parentPath = '') => {
      codes.forEach(code => {
        const fullPath = parentPath ? `${parentPath}.${code.code}` : code.code;
        result.push({
          ...code,
          fullGLPath: fullPath,
          segmentName: code.name,
          segmentCode: code.code
        });
        if (code.children && code.children.length > 0) {
          flatten(code.children, fullPath);
        }
      });
    };
    flatten(glCodes);
    return result;
  }, [glCodes]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name === 'scope' && value === 'Global') {
      setFormData({
        ...formData,
        scope: value,
        orgUnitId: '',
        orgUnitName: '',
        orgUnitPath: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleOrgUnitChange = (orgUnitId, orgUnit) => {
    setFormData({
      ...formData,
      orgUnitId,
      orgUnitName: orgUnit?.name || '',
      orgUnitPath: orgUnit?.path || ''
    });
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.assigneeId) newErrors.assigneeId = `Please select a ${formData.type}`;
    if (!formData.glCode) newErrors.glCode = 'Please select a GL code';
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Please select an organisation unit';
    }
    if (formData.upperLimit <= formData.lowerLimit) {
      newErrors.upperLimit = 'Upper limit must be greater than lower limit';
    }
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (formData.requiresFourEyes && !formData.secondApproverId) {
      newErrors.secondApproverId = 'Please select a second approver';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9629">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9630">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9631">
          {delegation ? 'Edit Delegation' : 'Create Delegation'}
        </h3>
      </div>
      <form onSubmit={handleSubmit} data-id="element-9632">
        <div className="px-4 py-5 sm:p-6" data-id="element-9633">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9634">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-9635">
              <label className="block text-sm font-medium text-gray-700" data-id="element-9636">
                Scope <span className="text-red-500" data-id="element-9637">*</span>
              </label>
              <div className="mt-1" data-id="element-9638">
                <select name="scope" value={formData.scope} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9639">
                  <option value="Global" data-id="element-9640">Global</option>
                  <option value="OrgUnit" data-id="element-9641">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-9642">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-9643">
                <OrgUnitSelector value={formData.orgUnitId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-9644" />
              </div>}

            {/* Rest of form fields - keeping existing structure */}
            {/* Assignment type, assignee, GL code, limits, etc. */}
            <div className="sm:col-span-6" data-id="element-9645">
              <p className="text-sm text-gray-500" data-id="element-9646">
                Form fields for assignment, GL code, limits, and approval
                requirements would continue here...
              </p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-9647">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-9648">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-9649">
            {delegation ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>;
}